import { Component, Input } from '@angular/core';
import {
  ActionType,
  ErrorGroup,
  ErrorType,
  PageValidationType,
  StartAndEnd,
  ValidationError,
  ValidationResult
} from '../../../../models/projects/validation.models';
import { Events, NavController, NavParams, ViewController } from 'ionic-angular';
import { GapOverlapValidationPage } from '../../../validation/gap-overlap/gap-overlap';
import { DailyLogOperationInstance } from '../../../../models/projects/operation.models';
import { ContractualHierarchyPage } from '../../../validation/contractual-hierarchy/contractual-hierarchy';
import { OperationService } from '../../../../services/operations/operation/operation.service';
import { OpenEndedValidationPage } from '../../../validation/open-ended/open-ended';
import { MultiDayValidationPage } from '../../../validation/multi-day/multi-day';
import {
  DailyProgressReportPreviewPage
} from '../../../daily-progress-report/daily-progress-report-preview/daily-progress-report-preview';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { DailyLogService } from '../../../../services/daily-log/daily-log.service';
import { DailyLogEntriesService } from '../../../../services/daily-log/daily-log-entries/daily-log-entries.service';
import { ValidationService } from '../../../../services/validation/validation.service';
import { DailyLogPermissions } from '../../../../models/permissions/permission.models';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import {
  MorningMinutesReportPreviewPage
} from '../../../morning-minutes/morning-minutes-report-preview/morning-minutes-report-preview';
import { NavHelperService } from '../../../../services/nav/nav-helper.service';
import { AppConfig } from '../../../../app/app.config';
import {
  FieldPositioningOutstandingPage
} from '../../../validation/field-positioning-outstanding/field-positioning-outstanding';
import { RobFigureOutstandingPage } from '../../../validation/rob-figure-outstanding/rob-figure-outstanding';
import { isIncompleteSection } from '../../../../services/validation/incomplete-sections';
import { menuSectionFor } from '../../../../services/nav/menu-sections';
import { SectionNavigationService } from '../../../../services/nav/section-navigation.service';

@Component({
  selector: 'app-errors',
  templateUrl: './errors.component.html',
})
export class ErrorsComponent {
  @Input() showActionButtons: boolean;
  @Input() embedded: boolean = false;
  @Input() reportingDateLabel: string;
  @Input() showEmbeddedEmptyState: boolean = false;

  config = AppConfig.settings;
  validationResult: ValidationResult = new ValidationResult();
  validationLoading: boolean;
  errors: ValidationError[] = [];
  warnings: ValidationError[] = [];
  preventRefresh: boolean = false;
  errorGroups: ErrorGroup[] = [];
  warningGroups: ErrorGroup[] = [];
  availableSections: PageValidationType[] = [];
  openingSection: boolean = false;
  sectionNavigationError: string;
  overlapData: StartAndEnd[] = [];
  instances: DailyLogOperationInstance[] = [];
  completeDay: boolean = false;
  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  permissions: DailyLogPermissions = new DailyLogPermissions();

  constructor(
    params: NavParams,
    public viewController: ViewController,
    public navCtrl: NavController,
    public navParams: NavParams,
    public events: Events,
    public operationService: OperationService,
    public dailyLogService: DailyLogService,
    private validationService: ValidationService,
    private permissionService: PermissionsService,
    private navHelperService: NavHelperService,
    private sectionNavigation: SectionNavigationService,
    private dailyLogEntriesService: DailyLogEntriesService,
  ) {
    this.validationResult = params.get('validationResult');
    this.overlapData = params.get('overlapTimes');
    this.instances = params.get('instances');
    this.completeDay = params.get('completeDay');

    const date = params.get('date');
    const shouldRefresh = params.get('refresh');

    this.permissionService
      .dailyLogPermissions()
      .then(res => {
        this.permissions = res;
      });

    if (!!date) {
      this.dailyLogDate = date;
    }

    if (shouldRefresh) {
      this.createErrors();
      // errors-page.ts is calling refreshErrors() which in turn re-runs the createErrors() method, if it has already run here we don't
      // need to call it again
      this.preventRefresh = true;
    }

    events.subscribe('report generated', () => {
      this.removeMorningMinutesWarning();
    });
  }

  get showEmptyState(): boolean {
    return (!this.embedded || this.showEmbeddedEmptyState === true) && !this.validationLoading
      && this.validationResult?.errors?.length === 0
      && this.errors.length === 0 && this.warnings.length === 0;
  }

  createErrors() {
    this.errorGroups = [];
    this.errors = [];
    this.warningGroups = [];
    this.warnings = [];

    this.errors = this.validationResult.errors
      .filter(p => p.type === ErrorType.Error || p.type === ErrorType.Default || !p.type);

    const incompleteSections = this.errors.filter(isIncompleteSection);
    const detailedErrors = this.errors.filter(error => !isIncompleteSection(error));
    this.availableSections = [];
    this.sectionNavigationError = null;
    if (incompleteSections.length) {
      this.sectionNavigation.availableSections().then(sections => this.availableSections = sections)
        .catch(() => this.availableSections = []);
    }

    const contractualErrors = detailedErrors
      .filter(p => p.actionType === ActionType.OperationGap
        || p.actionType === ActionType.OperationOverlap);

    this.overlapData = contractualErrors.filter(p => !!p.startAndEnd).map(p => p.startAndEnd);

    const contractualErrorGroup = new ErrorGroup();
    contractualErrorGroup.errors = contractualErrors;
    contractualErrorGroup.title = `Contractual Operations (${contractualErrorGroup.errors.length})`;
    contractualErrorGroup.action = ActionType.OperationGap;

    if (contractualErrorGroup.errors.length > 0) {
      this.errorGroups.push(contractualErrorGroup);
    }

    if (incompleteSections.length) {
      const group = new ErrorGroup();
      group.title = `Incomplete Sections (${incompleteSections.length})`;
      group.errors = incompleteSections;
      group.incompleteSections = incompleteSections;
      this.errorGroups.push(group);
    }

    const weatherErrors = detailedErrors.filter(p => p.actionType === ActionType.Weather);
    const weatherErrorGroup = new ErrorGroup();
    weatherErrorGroup.errors = weatherErrors;
    weatherErrorGroup.title = `Weather Reading Errors (${weatherErrorGroup.errors.length})`;
    weatherErrorGroup.action = null;

    if (weatherErrorGroup.errors.length > 0) {
      this.errorGroups.push(weatherErrorGroup);
    }

    const safetyErrors = detailedErrors.filter(p => p.actionType === ActionType.Safety);
    const safetyErrorGroup = new ErrorGroup();
    safetyErrorGroup.errors = safetyErrors;
    safetyErrorGroup.title = `HSEQ Errors (${safetyErrorGroup.errors.length})`;
    safetyErrorGroup.action = null;

    if (safetyErrorGroup.errors.length > 0) {
      this.errorGroups.push(safetyErrorGroup);
    }

    const consumableErrors = detailedErrors.filter(p => p.actionType === ActionType.Consumable);
    const consumableErrorGroup = new ErrorGroup();
    consumableErrorGroup.errors = consumableErrors;
    consumableErrorGroup.title = `Consumable Errors (${consumableErrorGroup.errors.length})`;
    consumableErrorGroup.action = null;

    if (consumableErrorGroup.errors.length > 0) {
      this.errorGroups.push(consumableErrorGroup);
    }

    const vesselCrewErrors = detailedErrors.filter(p => p.actionType === ActionType.VesselCrew);
    const vesselCrewErrorGroup = new ErrorGroup();
    vesselCrewErrorGroup.errors = vesselCrewErrors;
    vesselCrewErrorGroup.title = `POB Errors (${vesselCrewErrorGroup.errors.length})`;
    vesselCrewErrorGroup.action = null;

    if (vesselCrewErrorGroup.errors.length > 0) {
      this.errorGroups.push(vesselCrewErrorGroup);
    }

    const operationRuleErrors = detailedErrors.filter(p => p.actionType === ActionType.OperationRule);
    const operationRuleErrorGroup = new ErrorGroup();
    operationRuleErrorGroup.errors = operationRuleErrors;
    operationRuleErrorGroup.title = `Operation Rule Errors (${operationRuleErrorGroup.errors.length})`;
    operationRuleErrorGroup.action = null;

    if (operationRuleErrorGroup.errors.length > 0) {
      this.errorGroups.push(operationRuleErrorGroup);
    }

    const timeZoneErrors = detailedErrors.filter(p => p.actionType === ActionType.Timezone);
    const timeZoneErrorGroup = new ErrorGroup();
    timeZoneErrorGroup.errors = timeZoneErrors;
    timeZoneErrorGroup.title = `Time Zone Errors (${timeZoneErrorGroup.errors.length})`;
    timeZoneErrorGroup.action = null;

    if (timeZoneErrorGroup.errors.length > 0) {
      this.errorGroups.push(timeZoneErrorGroup);
    }

    const morningMinutesErrors = detailedErrors.filter(p => p.actionType === ActionType.MorningMinutes);
    const morningMinutesErrorGroup = new ErrorGroup();
    morningMinutesErrorGroup.errors = morningMinutesErrors;
    morningMinutesErrorGroup.title = `Morning Minutes (${morningMinutesErrorGroup.errors.length})`;
    morningMinutesErrorGroup.action = ActionType.MorningMinutes;

    if (morningMinutesErrorGroup.errors.length > 0) {
      this.errorGroups.push(morningMinutesErrorGroup);
    }

    const otherErrors = detailedErrors.filter(p =>
      p.actionType !== ActionType.OperationGap &&
      p.actionType !== ActionType.OperationOverlap &&
      p.actionType !== ActionType.Weather &&
      p.actionType !== ActionType.Safety &&
      p.actionType !== ActionType.Consumable &&
      p.actionType !== ActionType.OperationRule &&
      p.actionType !== ActionType.VesselCrew &&
      p.actionType !== ActionType.MorningMinutes &&
      p.actionType !== ActionType.Timezone
    );

    const otherErrorGroup = new ErrorGroup();
    otherErrorGroup.errors = otherErrors;
    otherErrorGroup.title = `Other (${otherErrorGroup.errors.length})`;

    if (otherErrorGroup.errors.length > 0) {
      this.errorGroups.push(otherErrorGroup);
    }

    this.warnings = this.validationResult.errors
      .filter(p => p.type === ErrorType.Warning);

    const hierarchyWarnings = this.warnings.filter(p => p.actionType === ActionType.ContractualHierarchy);
    const hierarchyWarningGroup = new ErrorGroup();
    hierarchyWarningGroup.errors = hierarchyWarnings;
    hierarchyWarningGroup.title = `Contractual Operations Hierarchy (${hierarchyWarningGroup.errors.length})`;
    hierarchyWarningGroup.action = ActionType.ContractualHierarchy;
    if (hierarchyWarningGroup.errors.length > 0) {
      this.warningGroups.push(hierarchyWarningGroup);
    }

    const multidayWarnings = this.warnings.filter(p => p.actionType === ActionType.Multiday);
    const multidayWarningGroup = new ErrorGroup();
    multidayWarningGroup.errors = multidayWarnings;
    multidayWarningGroup.title = `Multiple Days (${multidayWarningGroup.errors.length})`;
    multidayWarningGroup.action = ActionType.Multiday;
    if (multidayWarningGroup.errors.length > 0) {
      this.warningGroups.push(multidayWarningGroup);
    }

    const timeZoneWarnings = this.warnings.filter(p => p.actionType === ActionType.Timezone);
    const timeZoneWarningGroup = new ErrorGroup();
    timeZoneWarningGroup.errors = timeZoneWarnings;
    timeZoneWarningGroup.title = `Time Zone Change (${timeZoneWarningGroup.errors.length})`;
    timeZoneWarningGroup.action = null;
    if (timeZoneWarningGroup.errors.length > 0) {
      this.warningGroups.push(timeZoneWarningGroup);
    }

    const openEndedWarnings = this.warnings.filter(p => p.actionType === ActionType.OpenEnded);
    const openEndedWarningGroup = new ErrorGroup();
    openEndedWarningGroup.errors = openEndedWarnings;
    openEndedWarningGroup.title = `Open Ended Operations & Steps (${openEndedWarningGroup.errors.length})`;
    openEndedWarningGroup.action = ActionType.OpenEnded;
    if (openEndedWarningGroup.errors.length) {
      this.warningGroups.push(openEndedWarningGroup);
    }

    const dailyObservation = this.warnings.filter(p => p.actionType === ActionType.DailyObservations);
    const dailyObservationGroup = new ErrorGroup();
    dailyObservationGroup.errors = dailyObservation;
    dailyObservationGroup.title = `Daily Observation (${dailyObservationGroup.errors.length})`;
    if (dailyObservationGroup.errors.length > 0) {
      this.warningGroups.push(dailyObservationGroup);
    }

    const voyageLogWarnings = this.warnings.filter(p => p.actionType === ActionType.VoyageLog);
    const voyageLogWarningGroup = new ErrorGroup();
    voyageLogWarningGroup.errors = voyageLogWarnings;
    voyageLogWarningGroup.title = `Voyage Log (${voyageLogWarningGroup.errors.length})`;
    voyageLogWarningGroup.action = null;

    if (voyageLogWarningGroup.errors.length > 0) {
      this.warningGroups.push(voyageLogWarningGroup);
    }

    const fieldPositioningWarnings = this.warnings.filter(p => p.actionType === ActionType.FieldPositioningOutstanding);
    const fieldPositioningWarningGroup = new ErrorGroup();
    fieldPositioningWarningGroup.errors = fieldPositioningWarnings;
    fieldPositioningWarningGroup.title = `Field Positioning`;
    fieldPositioningWarningGroup.action = ActionType.FieldPositioningOutstanding;
    fieldPositioningWarningGroup.showButtonOverride = true;

    if (fieldPositioningWarningGroup.errors.length > 0) {
      this.warningGroups.push(fieldPositioningWarningGroup);
    }

    const robFigureWarnings = this.warnings.filter(p => p.actionType === ActionType.RobFuelFigures);
    const robFigureWarningGroup = new ErrorGroup();
    robFigureWarningGroup.errors = robFigureWarnings;
    robFigureWarningGroup.title = `ROB Fuel Figures`;
    robFigureWarningGroup.action = ActionType.RobFuelFigures;
    robFigureWarningGroup.showButtonOverride = true;

    if (robFigureWarningGroup.errors.length > 0) {
      this.warningGroups.push(robFigureWarningGroup);
    }

    const otherWarnings = this.warnings.filter(p =>
      p.actionType !== ActionType.OpenEnded &&
      p.actionType !== ActionType.Timezone &&
      p.actionType !== ActionType.ContractualHierarchy &&
      p.actionType !== ActionType.Multiday &&
      p.actionType !== ActionType.DailyObservations &&
      p.actionType !== ActionType.MorningMinutes &&
      p.actionType !== ActionType.VoyageLog &&
      p.actionType !== ActionType.FieldPositioningOutstanding &&
      p.actionType !== ActionType.RobFuelFigures
    );

    const otherWarningGroup = new ErrorGroup();
    otherWarningGroup.errors = otherWarnings;
    otherWarningGroup.title = `Other (${otherWarningGroup.errors.length})`;

    if (otherWarningGroup.errors.length) {
      this.warningGroups.push(otherWarningGroup);
    }

    this.validationLoading = false;
  }

  async refreshErrors() {
    if (this.embedded) {
      this.validationLoading = true;
      try {
        const data = await this.dailyLogService.getDailyLogDataForDay(this.dailyLogDate);
        const records = await this.dailyLogEntriesService.getRecordsIncludingContinuingOperations(data);
        this.instances = await this.operationService.getOperationsFromLogs(records);
        this.validationResult = await this.validationService.validateDailyLog(records, this.dailyLogDate, this.instances);
        this.createErrors();
      } finally {
        this.validationLoading = false;
      }
      return;
    }
    if (this.preventRefresh) {
      return;
    }

    if (!!this.instances && this.instances.length > 0) {
      this.validationLoading = true;

      const dailyLogPtDate = await this.dailyLogService.getCurrentLogDay();
      const data = await this.dailyLogService.getDailyLogDataForDay(dailyLogPtDate);
      const records = await this.dailyLogEntriesService.getRecordsIncludingContinuingOperations(data);
      this.instances = await this.operationService.getOperationsFromLogs(records);

      this.validationResult = await this.validationService.validateDailyLog(records, dailyLogPtDate, this.instances);
      this.dailyLogDate = dailyLogPtDate;

      this.createErrors();
    }
  }

  incompleteSectionLabel(error: ValidationError): string {
    return menuSectionFor(error.pageValidationType).validationLabel;
  }

  async openIncompleteSection(error: ValidationError) {
    if (this.openingSection || !isIncompleteSection(error)) { return; }
    this.openingSection = true;
    this.sectionNavigationError = null;
    try {
      this.sectionNavigationError = await this.sectionNavigation.open(error.pageValidationType, this.dailyLogDate);
    } catch {
      this.sectionNavigationError = 'Unable to open this section. Please try again.';
    } finally {
      this.openingSection = false;
    }
  }

  takeAction(type: ActionType) {
    // if user takes an action here, reload daily log when user returns to it
    this.navHelperService.updateDailyLogRequiresReload(true);

    switch (type) {
      case ActionType.OperationGap:
        this.navCtrl.push(GapOverlapValidationPage, {instances: this.instances, dailyLogDate: this.dailyLogDate});
        break;
      case ActionType.ContractualHierarchy:
        this.navCtrl.push(ContractualHierarchyPage, {instances: this.instances, dailyLogDate: this.dailyLogDate});
        break;
      case ActionType.OpenEnded:
        this.navCtrl.push(OpenEndedValidationPage, {instances: this.instances});
        break;
      case ActionType.Multiday:
        this.navCtrl.push(MultiDayValidationPage, {instances: this.instances});
        break;
      case ActionType.MorningMinutes:
        this.navCtrl.push(MorningMinutesReportPreviewPage, {date: this.dailyLogDate, editor: null, amending: false});
        break;
      case ActionType.FieldPositioningOutstanding:
        this.navCtrl.push(FieldPositioningOutstandingPage)
        break;
      case ActionType.RobFuelFigures:
        this.navCtrl.push(RobFigureOutstandingPage)
        break;
    }
  }

  closeModal() {
    this.viewController.dismiss();
  }

  previewDpr() {
    this.navCtrl.push(DailyProgressReportPreviewPage, {date: this.dailyLogDate, completeDay: this.completeDay});
  }

  removeMorningMinutesWarning() {
    const index = this.warningGroups.findIndex(p => p.action === ActionType.MorningMinutes);
    this.warningGroups.splice(index, 1);
  }
}



// WEBPACK FOOTER //
// ./src/pages/shared/components/errors/errors.component.ts